/**
 * Comp Report Service
 * Fetches and aligns film tracking data for comparative reporting by weeks before release
 * Supports both current film tracking and legacy Quorum data
 */

import apiService from './apiService';
import legacyQuorumService from './legacyQuorumService';
import { parseDateFromAPI } from '../utils/dateUtils';
import { transformSurveyWave } from '../utils/filmMetricsUtils';

/**
 * Calculate weeks before release from survey date and release date
 * @param {string|Date} surveyDate - Survey completion date
 * @param {string|Date} releaseDate - Film release date
 * @returns {number} - Weeks before release (positive = before, negative = after)
 */
export const calculateWeeksBeforeRelease = (surveyDate, releaseDate) => {
  const survey = parseDateFromAPI(surveyDate);
  const release = parseDateFromAPI(releaseDate);
  
  if (!survey || !release) return null;
  
  const diffTime = release.getTime() - survey.getTime();
  const diffDays = diffTime / (1000 * 60 * 60 * 24);
  const weeks = Math.round(diffDays / 7);
  
  return weeks;
};

/**
 * Transform legacy Quorum survey data to match current format
 * @param {Object} legacySurvey - Legacy survey data
 * @param {Object} filmInfo - Film information (release date, imdb_id, title)
 * @returns {Object} - Transformed survey wave
 */
const transformLegacySurvey = (legacySurvey, filmInfo) => {
  try {
    // Parse survey date (format: YYYYMMDD or Date object)
    let surveyDate = null;
    if (legacySurvey.survey_date) {
      if (typeof legacySurvey.survey_date === 'string') {
        // Handle YYYYMMDD format
        if (legacySurvey.survey_date.length === 8 && /^\d+$/.test(legacySurvey.survey_date)) {
          const year = legacySurvey.survey_date.substring(0, 4);
          const month = legacySurvey.survey_date.substring(4, 6);
          const day = legacySurvey.survey_date.substring(6, 8);
          surveyDate = new Date(`${year}-${month}-${day}`);
        } else {
          surveyDate = parseDateFromAPI(legacySurvey.survey_date);
        }
      } else if (legacySurvey.survey_date.value) {
        surveyDate = parseDateFromAPI(legacySurvey.survey_date.value);
      } else {
        surveyDate = parseDateFromAPI(legacySurvey.survey_date);
      }
    }
    
    // Get release date from film info
    const releaseDate = filmInfo.releaseDate || null;
    
    // Calculate weeks before release
    const weeksBeforeRelease = calculateWeeksBeforeRelease(surveyDate, releaseDate);
    
    // Map legacy metrics to current format
    // Legacy: awareness, interest, location, paytosee (all percentages)
    // Current: pctAware, pctInterest, pctTheater, pctWillingToPay
    return {
      // Identifiers
      imdbId: filmInfo.imdbId,
      title_text: filmInfo.title,
      FilmRecencyOrder: legacySurvey.survey_order || 999, // Use survey_order if available
      
      // Dates
      SurveyCompleteDate: surveyDate ? surveyDate.toISOString().split('T')[0] : null,
      surveyCompleteDateRaw: surveyDate,
      releasedate: releaseDate ? (releaseDate instanceof Date ? releaseDate.toISOString().split('T')[0] : releaseDate) : null,
      releaseDateRaw: releaseDate,
      
      // Calculated fields
      weeksUntilRelease: weeksBeforeRelease,
      daysUntilRelease: weeksBeforeRelease !== null ? weeksBeforeRelease * 7 : null,
      
      // Metrics (convert percentages to match current format)
      // Legacy percentages are already 0-100, current expects 0-100
      pctAware: legacySurvey.awareness !== null && legacySurvey.awareness !== undefined ? legacySurvey.awareness : null,
      pctInterest: legacySurvey.interest !== null && legacySurvey.interest !== undefined ? legacySurvey.interest : null,
      pctTheater: legacySurvey.location !== null && legacySurvey.location !== undefined ? legacySurvey.location : null,
      pctWillingToPay: legacySurvey.paytosee !== null && legacySurvey.paytosee !== undefined ? legacySurvey.paytosee : null,
      
      // Legacy doesn't have these metrics, set to null
      pctHeat: null,
      pctPLF: null,
      pctInterestOfAware: null,
      pctInterestNotAware: null,
      
      // Data source flag
      dataSource: 'legacy_quorum',
      isLegacy: true
    };
  } catch (error) {
    console.error('❌ Failed to transform legacy survey:', error, legacySurvey);
    return null;
  }
};

/**
 * Fetch legacy Quorum data for a film by IMDB ID
 * @param {string} imdbId - IMDB ID of the film
 * @returns {Promise<Array>} - Array of transformed survey wave data
 */
const fetchLegacyQuorumData = async (imdbId) => {
  try {
    console.log('📊 Fetching legacy Quorum data for:', imdbId);
    
    // First, find the film by imdb_id in the legacy films list
    const filmsResponse = await legacyQuorumService.getFilms({
      imdb_id: imdbId,
      limit: 1
    });
    
    if (!filmsResponse.success || !filmsResponse.data?.films || filmsResponse.data.films.length === 0) {
      console.log('⚠️ Film not found in legacy Quorum:', imdbId);
      return [];
    }
    
    const legacyFilm = filmsResponse.data.films[0];
    const filmId = legacyFilm.film_id;
    
    // Get detailed film data with surveys
    const filmDetailsResponse = await legacyQuorumService.getFilmDetails(filmId);
    
    if (!filmDetailsResponse.success || !filmDetailsResponse.data?.film) {
      console.warn('⚠️ Failed to fetch legacy film details:', filmId);
      return [];
    }
    
    const filmData = filmDetailsResponse.data.film;
    const surveys = filmData.surveys || [];
    
    if (surveys.length === 0) {
      console.log('⚠️ No surveys found for legacy film:', imdbId);
      return [];
    }
    
    // Build release date from film data
    const releaseDate = filmData.release_year && filmData.release_month && filmData.release_day
      ? new Date(filmData.release_year, filmData.release_month - 1, filmData.release_day)
      : null;
    
    const filmInfo = {
      imdbId,
      title: filmData.film_name || legacyFilm.film_name,
      releaseDate
    };
    
    // Transform all surveys
    const transformedWaves = surveys
      .map(survey => transformLegacySurvey(survey, filmInfo))
      .filter(wave => wave !== null);
    
    console.log('✅ Fetched legacy Quorum data:', {
      imdbId,
      filmId,
      surveyCount: surveys.length,
      transformedCount: transformedWaves.length
    });
    
    return transformedWaves;
  } catch (error) {
    console.error('❌ Failed to fetch legacy Quorum data for', imdbId, ':', error);
    return [];
  }
};

/**
 * Fetch film tracking data for a specific film
 * Fetches from both current tracking and legacy Quorum endpoints
 * @param {string} imdbId - IMDB ID of the film
 * @param {Object} options - Optional filters for demographic data
 * @returns {Promise<Array>} - Array of survey wave data (merged from both sources)
 */
export const fetchFilmTrackingData = async (imdbId, options = {}) => {
  try {
    console.log('📊 Fetching film tracking data for:', imdbId, options);
    
    // Fetch from both sources in parallel
    const [currentData, legacyData] = await Promise.all([
      // Current film tracking endpoint
      (async () => {
        try {
          // Build group_by parameter - apiService.getFilmSurveyToplines already has a default,
          // but we can override it if needed for demographic breakdowns
          const filters = {
            aggregate: 'true'
          };
          
          // Only override group_by if we need demographic data
          if (options.demographic) {
            filters.group_by = `imdb_id,FilmRecencyOrder,title_text,SurveyCompleteDate,releasedate,${options.demographic}`;
          }
          // Otherwise, let apiService use its default group_by
          
          // Add demographic filter if specified
          if (options.demographicFilter) {
            filters[options.demographic] = options.demographicFilter;
          }
          
          const response = await apiService.getFilmSurveyToplines(imdbId, filters);
          
          if (!response.success || !Array.isArray(response.data) || response.data.length === 0) {
            return [];
          }
          
          // Transform the survey waves
          const transformedWaves = response.data
            .map((wave, index) => {
              try {
                const transformed = transformSurveyWave(wave);
                if (!transformed) {
                  console.warn(`⚠️ Transformation returned null/undefined for wave ${index}:`, wave);
                }
                return transformed;
              } catch (transformError) {
                console.error(`❌ Failed to transform wave ${index}:`, transformError, wave);
                return null;
              }
            })
            .filter(wave => wave !== null && wave !== undefined);
          
          return transformedWaves;
        } catch (error) {
          console.warn('⚠️ Failed to fetch current tracking data:', error);
          return [];
        }
      })(),
      // Legacy Quorum endpoint (only if not requesting demographic breakdowns)
      options.demographic ? Promise.resolve([]) : fetchLegacyQuorumData(imdbId)
    ]);
    
    // Merge data from both sources
    // Current data takes precedence (has more recent data and more metrics)
    // Legacy data fills in gaps for older films
    const allWaves = [...currentData];
    
    // Add legacy waves that don't overlap with current data
    // Group by weeks before release to avoid duplicates
    const currentWeeks = new Set(currentData.map(w => w.weeksUntilRelease).filter(w => w !== null && w !== undefined));
    
    legacyData.forEach(legacyWave => {
      const weeks = legacyWave.weeksUntilRelease;
      // Only add if we don't have current data for this week
      if (weeks !== null && weeks !== undefined && !currentWeeks.has(weeks)) {
        allWaves.push(legacyWave);
      }
    });
    
    console.log('✅ Fetched film tracking data (merged):', {
      imdbId,
      currentWaveCount: currentData.length,
      legacyWaveCount: legacyData.length,
      totalWaveCount: allWaves.length,
      hasDemographic: !!options.demographic,
      sampleWave: allWaves.length > 0 ? {
        weeksUntilRelease: allWaves[0].weeksUntilRelease,
        dataSource: allWaves[0].dataSource || 'current',
        pctAware: allWaves[0].pctAware
      } : null
    });
    
    return allWaves;
  } catch (error) {
    console.error('❌ Failed to fetch film tracking data for', imdbId, ':', error);
    console.error('Error details:', {
      message: error.message,
      status: error.status,
      stack: error.stack
    });
    return [];
  }
};

/**
 * Align film data by weeks before release
 * @param {Array} surveyWaves - Array of survey wave data for a film
 * @param {string} imdbId - IMDB ID of the film
 * @param {string} filmTitle - Title of the film
 * @returns {Object} - Film data aligned by weeks before release
 */
export const alignFilmDataByWeeks = (surveyWaves, imdbId, filmTitle) => {
  if (!Array.isArray(surveyWaves) || surveyWaves.length === 0) {
    return {
      imdbId,
      filmTitle,
      releaseDate: null,
      dataByWeeks: {}
    };
  }
  
  // Get release date from first wave (all waves should have same release date)
  const releaseDate = surveyWaves[0]?.releaseDate || surveyWaves[0]?.releaseDateRaw;
  
  // Group waves by weeks before release
  const dataByWeeks = {};
  
  surveyWaves.forEach(wave => {
    // Use the already-calculated weeksUntilRelease if available, otherwise calculate it
    let weeksBeforeRelease = wave.weeksUntilRelease;
    
    if (weeksBeforeRelease === undefined || weeksBeforeRelease === null) {
      // Fallback to calculating it if not present
      weeksBeforeRelease = calculateWeeksBeforeRelease(
        wave.surveyCompleteDate || wave.surveyCompleteDateRaw,
        wave.releaseDate || wave.releaseDateRaw
      );
    }
    
    // Include all weeks, even negative ones (for films already released)
    if (weeksBeforeRelease !== null && weeksBeforeRelease !== undefined && !isNaN(weeksBeforeRelease)) {
      if (!dataByWeeks[weeksBeforeRelease]) {
        dataByWeeks[weeksBeforeRelease] = [];
      }
      dataByWeeks[weeksBeforeRelease].push(wave);
    } else {
      console.warn('⚠️ Could not determine weeks before release for wave:', {
        imdbId,
        surveyDate: wave.surveyCompleteDate || wave.surveyCompleteDateRaw,
        releaseDate: wave.releaseDate || wave.releaseDateRaw,
        weeksUntilRelease: wave.weeksUntilRelease,
        calculatedWeeks: weeksBeforeRelease
      });
    }
  });
  
  // For each week bucket, use the most recent wave (lowest FilmRecencyOrder)
  const alignedData = {};
  Object.keys(dataByWeeks).forEach(weeks => {
    const waves = dataByWeeks[weeks];
    // Sort by FilmRecencyOrder (1 = most recent) and take the first one
    const mostRecentWave = waves.sort((a, b) => 
      (a.FilmRecencyOrder || 999) - (b.FilmRecencyOrder || 999)
    )[0];
    alignedData[weeks] = mostRecentWave;
  });
  
  return {
    imdbId,
    filmTitle,
    releaseDate,
    dataByWeeks: alignedData
  };
};

/**
 * Fetch and align data for primary film and all comp films
 * @param {string} primaryFilmId - IMDB ID of the primary film
 * @param {string} primaryFilmTitle - Title of the primary film
 * @param {Array} compFilms - Array of comp film objects with imdbId and compFilmTitle
 * @returns {Promise<Object>} - Aligned data for all films
 */
export const fetchCompReportData = async (primaryFilmId, primaryFilmTitle, compFilms = []) => {
  try {
    console.log('📊 Fetching comp report data:', {
      primaryFilmId,
      primaryFilmTitle,
      compCount: compFilms.length,
      compIds: compFilms.map(c => c.compFilmId)
    });
    
    // Fetch data for primary film
    const primaryWaves = await fetchFilmTrackingData(primaryFilmId, {});
    console.log('📊 Primary film waves:', {
      filmId: primaryFilmId,
      waveCount: primaryWaves.length,
      weeks: primaryWaves.map(w => w.weeksUntilRelease)
    });
    
    const primaryAligned = alignFilmDataByWeeks(primaryWaves, primaryFilmId, primaryFilmTitle);
    console.log('📊 Primary film aligned:', {
      filmId: primaryFilmId,
      inputWaveCount: primaryWaves.length,
      weeksWithData: Object.keys(primaryAligned.dataByWeeks),
      weeksCount: Object.keys(primaryAligned.dataByWeeks).length,
      sampleWeekData: Object.keys(primaryAligned.dataByWeeks).length > 0 ? {
        week: Object.keys(primaryAligned.dataByWeeks)[0],
        data: primaryAligned.dataByWeeks[Object.keys(primaryAligned.dataByWeeks)[0]]
      } : null
    });
    
    // Fetch data for all comp films in parallel
    const compDataPromises = compFilms.map(async (comp) => {
      const waves = await fetchFilmTrackingData(comp.compFilmId, {});
      console.log('📊 Comp film waves:', {
        filmId: comp.compFilmId,
        waveCount: waves.length,
        weeks: waves.map(w => w.weeksUntilRelease)
      });
      const aligned = alignFilmDataByWeeks(waves, comp.compFilmId, comp.compFilmTitle || comp.compFilmId);
      console.log('📊 Comp film aligned:', {
        filmId: comp.compFilmId,
        weeksWithData: Object.keys(aligned.dataByWeeks)
      });
      return aligned;
    });
    
    const compAligned = await Promise.all(compDataPromises);
    
    // Get all unique weeks from all films
    const allWeeks = new Set();
    Object.keys(primaryAligned.dataByWeeks).forEach(week => allWeeks.add(parseInt(week, 10)));
    compAligned.forEach(comp => {
      Object.keys(comp.dataByWeeks).forEach(week => allWeeks.add(parseInt(week, 10)));
    });
    
    const sortedWeeks = Array.from(allWeeks).sort((a, b) => b - a); // Descending (12 weeks, 11 weeks, etc.)
    
    console.log('📊 Final comp report data:', {
      primaryFilm: {
        filmId: primaryFilmId,
        weeksWithData: Object.keys(primaryAligned.dataByWeeks),
        weeksCount: Object.keys(primaryAligned.dataByWeeks).length
      },
      compFilms: compAligned.map((c, idx) => ({
        filmId: compFilms[idx]?.compFilmId,
        weeksWithData: Object.keys(c.dataByWeeks),
        weeksCount: Object.keys(c.dataByWeeks).length
      })),
      allWeeks: sortedWeeks,
      hasData: sortedWeeks.length > 0,
      totalWeeks: sortedWeeks.length
    });
    
    if (sortedWeeks.length === 0) {
      console.error('❌ No weeks found in aligned data!');
      console.error('Primary film dataByWeeks:', primaryAligned.dataByWeeks);
      console.error('Comp films dataByWeeks:', compAligned.map(c => c.dataByWeeks));
      console.error('Primary waves sample:', primaryWaves.slice(0, 2));
    }
    
    return {
      primaryFilm: primaryAligned,
      compFilms: compAligned,
      weeks: sortedWeeks,
      metadata: {
        fetchedAt: new Date().toISOString(),
        primaryFilmId,
        primaryFilmTitle,
        compCount: compFilms.length
      }
    };
  } catch (error) {
    console.error('❌ Failed to fetch comp report data:', error);
    console.error('Error details:', {
      message: error.message,
      status: error.status,
      stack: error.stack
    });
    throw error;
  }
};

/**
 * Get metric value for a film at a specific week
 * @param {Object} filmData - Aligned film data
 * @param {number} weeksBeforeRelease - Weeks before release
 * @param {string} metric - Metric name (pctAware, pctInterest, etc.)
 * @returns {number|string} - Metric value or 'N/A' if not available
 */
export const getMetricAtWeek = (filmData, weeksBeforeRelease, metric) => {
  if (!filmData || !filmData.dataByWeeks) return 'N/A';
  
  const weekData = filmData.dataByWeeks[weeksBeforeRelease];
  if (!weekData) return 'N/A';
  
  const value = weekData[metric];
  if (value === null || value === undefined) return 'N/A';
  
  return typeof value === 'number' ? value : 'N/A';
};

export default {
  calculateWeeksBeforeRelease,
  fetchFilmTrackingData,
  alignFilmDataByWeeks,
  fetchCompReportData,
  getMetricAtWeek
};

